import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../api/axios';

interface ForgotPasswordForm {
  email: string;
}

export default function ForgotPassword() {
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setError(null);
      await axios.post('/auth/forgot-password', { email: data.email });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('An error occurred. Please try again.');
      }
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%'}}>
            <CardContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account exists with this email, you will receive password reset instructions.
              </Alert>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Return to login
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          e-BIRA
        </Typography>
        <Card sx={{ width: '100%', padding:2,border: '1px solid #ccc', borderRadius: 2}}>
          <CardContent>
            <Typography component="h2" variant="h5" sx={{ mb: 3 }}>
              Forgot Password
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                autoComplete="email"
                autoFocus
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Send Reset Instructions
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Back to login
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography component="small" sx={{ mt:5, mb: 4 }}>
          Copyright © {new Date().getFullYear()} <a href="https://ebira.rfsteknologi.site">ebira.rfsteknologi.site</a>. All rights reserved.
        </Typography>
        <Typography component="small" sx={{ mt:5, mb: 4, fontSize: '0.85rem'}}>
          Developed by <a href="https://rstehnologi.com" target="_blank">rstehnologi.com</a>
        </Typography>
      </Box>
    </Container>
  );
}
